import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasSaveActions,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  ClassificationStatus,
  HealthRecordItem,
  HealthRecordType,
  useUpdateHealthRecord,
} from '../hooks/use-health-records'
import { STATUS_LABELS, TYPE_LABELS } from './health-record-display'

type HealthRecordDraft = {
  title: string
  record_type: HealthRecordType
  record_date: string
  summary: string
  description: string
  classification_status: ClassificationStatus
  classification_reason: string
  classification_confidence: string
  visibility: string
}

interface HealthRecordEditCanvasProps {
  record: HealthRecordItem
  onClose: () => void
}

const RECORD_TYPE_OPTIONS: HealthRecordType[] = [
  'general_document',
  'test_result',
  'imaging',
  'referral',
  'prescription',
  'form',
  'correspondence',
  'receipt_or_invoice',
  'unknown',
]

const STATUS_OPTIONS: ClassificationStatus[] = [
  'pending',
  'processing',
  'classified',
  'needs_review',
  'unsupported',
  'failed',
]

function buildDraft(record: HealthRecordItem): HealthRecordDraft {
  return {
    title: record.title,
    record_type: record.record_type,
    record_date: record.record_date || '',
    summary: record.summary || '',
    description: record.description || '',
    classification_status: record.classification_status,
    classification_reason: record.classification_reason || '',
    classification_confidence:
      typeof record.classification_confidence === 'number'
        ? String(Math.round(record.classification_confidence * 100))
        : '',
    visibility: record.visibility || 'private',
  }
}

export function HealthRecordEditCanvas({ record, onClose }: HealthRecordEditCanvasProps) {
  const updateRecord = useUpdateHealthRecord()
  const initialDraft = useMemo(() => buildDraft(record), [record])
  const [draft, setDraft] = useState<HealthRecordDraft>(initialDraft)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    setDraft(buildDraft(record))
    setValidationError(null)
  }, [record])

  const isDirty = JSON.stringify(draft) !== JSON.stringify(initialDraft)

  const handleSave = () => {
    if (!draft.title.trim()) {
      setValidationError('Title is required')
      return
    }

    let confidence: number | null = null
    if (draft.classification_confidence.trim()) {
      const parsed = Number(draft.classification_confidence)
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
        setValidationError('Confidence must be between 0 and 100')
        return
      }
      confidence = parsed / 100
    }

    setValidationError(null)
    updateRecord.mutate(
      {
        id: record.id,
        title: draft.title.trim(),
        record_type: draft.record_type,
        record_date: draft.record_date || null,
        summary: draft.summary,
        description: draft.description,
        classification_status: draft.classification_status,
        classification_reason: draft.classification_reason,
        classification_confidence: confidence,
        visibility: draft.visibility,
      },
      {
        onSuccess: () => {
          toast.success('Health record updated')
          onClose()
        },
        onError: (error) => {
          toast.error(error instanceof Error ? error.message : 'Failed to update health record')
        },
      },
    )
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="space-y-2">
          <Label htmlFor="artifact-health-record-title">Title *</Label>
          <Input
            id="artifact-health-record-title"
            value={draft.title}
            onChange={(event) => {
              setDraft((current) => ({ ...current, title: event.target.value }))
              setValidationError(null)
            }}
            placeholder="Name this record"
            className="placeholder:italic"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Record type</Label>
            <Select
              value={draft.record_type}
              onValueChange={(value) => setDraft((current) => ({ ...current, record_type: value as HealthRecordType }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RECORD_TYPE_OPTIONS.map((type) => (
                  <SelectItem key={type} value={type}>
                    {TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="artifact-health-record-date">Record date</Label>
            <Input
              id="artifact-health-record-date"
              type="date"
              value={draft.record_date}
              onChange={(event) => setDraft((current) => ({ ...current, record_date: event.target.value }))}
              className="placeholder:italic"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-health-record-summary">Summary</Label>
          <Textarea
            id="artifact-health-record-summary"
            value={draft.summary}
            onChange={(event) => setDraft((current) => ({ ...current, summary: event.target.value }))}
            placeholder="Summarise the record"
            className="placeholder:italic"
            rows={4}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-health-record-description">Description</Label>
          <Textarea
            id="artifact-health-record-description"
            value={draft.description}
            onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
            placeholder="Add details or context"
            className="placeholder:italic"
            rows={5}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Classification status</Label>
            <Select
              value={draft.classification_status}
              onValueChange={(value) =>
                setDraft((current) => ({ ...current, classification_status: value as ClassificationStatus }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="artifact-health-record-confidence">Confidence %</Label>
            <Input
              id="artifact-health-record-confidence"
              type="number"
              min={0}
              max={100}
              value={draft.classification_confidence}
              onChange={(event) =>
                setDraft((current) => ({ ...current, classification_confidence: event.target.value }))
              }
              placeholder="e.g. 75"
              className="placeholder:italic"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-health-record-filing-reason">Filing reason</Label>
          <Textarea
            id="artifact-health-record-filing-reason"
            value={draft.classification_reason}
            onChange={(event) => setDraft((current) => ({ ...current, classification_reason: event.target.value }))}
            placeholder="Explain how this was filed"
            className="placeholder:italic"
            rows={3}
          />
        </div>

        <div className="space-y-2">
          <Label>Visibility</Label>
          <Select
            value={draft.visibility}
            onValueChange={(value) => setDraft((current) => ({ ...current, visibility: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="shared">Shared</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(validationError || updateRecord.error?.message) && (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {validationError || updateRecord.error?.message}
          </div>
        )}
      </form>

      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={updateRecord.isPending}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
